import { endOfDay, getTime, isEqual, startOfDay } from 'date-fns'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import Header from '@/components-latest/layout/Header'
import Banner from '@/components-latest/primitives/Banner'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import {
  CalendarFilled,
  InfoCircle
} from '@/components-latest/primitives/Icons'
import Text from '@/components-latest/primitives/Text'
import Tabs, { Tab } from '@/components-latest/shared/Tabs'
import BestSellingPerformance from '@/components-latest/ui/PerformancePage/BestSellingPerformance'
import DateRangeSelect, {
  CalendarRange,
  PERIOD_MAP
} from '@/components-latest/ui/PerformancePage/DateRangeSelect'
import EmptyPerformance from '@/components-latest/ui/PerformancePage/EmptyPerformance'
import OrdersPerformance from '@/components-latest/ui/PerformancePage/OrdersPerformance'
import PreparationTime from '@/components-latest/ui/PerformancePage/PreparationTime'
import RevenuePerformance from '@/components-latest/ui/PerformancePage/RevenuePerformance'
import ReviewsPerformance from '@/components-latest/ui/PerformancePage/ReviewPerformance'
import {
  useLazyFetchBestSellingPerformanceQuery,
  useLazyFetchBranchBestSellingPerformanceQuery,
  useLazyFetchReviewsPerformanceQuery,
  useLazyFetchBranchRevenuePerformanceQuery,
  useLazyFetchOrdersPerformanceQuery,
  useLazyFetchPerformancePreparationTimeQuery,
  useLazyFetchRevenuePerformanceQuery,
  useLazyFetchBranchReviewsPerformanceQuery,
  useLazyFetchOrdersPerformanceOldQuery,
  useLazyFetchPreparationTimePerformanceOldQuery
} from '@/ducks/performance/api/performance.api'
import { OrdersPerformance as OrdersPerformanceData } from '@/ducks/performance/performance.types'
import { BestSellingResponse } from '@/ducks/performance/performance.types'
import { useFetchAllStoreBranchesQuery } from '@/ducks/store/api/store.api'
import {
  selectStoreId,
  selectStoreName
} from '@/ducks/store/selectors/store.selectors'

import ComparisonSelector from './ComparisonSelector'
import Styles from './PerformancePage.module.scss'
import Spinner, { LoadingSpinner } from '@/components-latest/primitives/Spinner'
import { promise } from 'zod'
import clsx from 'clsx'
import Store from '@/components-latest/primitives/Icons/Store'


const DEFAULT_PERIOD = PERIOD_MAP.DAILY
const DAY_IN_MS = 86400000

type RouteParams = {
  id?: 'orders' | 'revenue' | 'quality'
}

interface OptionType {
  store_id: string
  restaurantname: string
  restaurant_name_i18n?: {
    en: string
    fr: string
    ar: string
  }
  availability?: number
  address?: string
  area: string
}

const PerformancePage = () => {
  const { t } = useTranslation()
  const { id = 'orders' } = useParams<RouteParams>()
  const { store_web_app_branches_performance } = useFlags()
  const [fetchOrdersPerformance] = useLazyFetchOrdersPerformanceQuery()
  const [fetchOrdersPerformanceOld] = useLazyFetchOrdersPerformanceOldQuery()
  const [fetchPreparationTimePerformanceOld] = useLazyFetchPreparationTimePerformanceOldQuery()
  const { data: branches } = useFetchAllStoreBranchesQuery()
  const parentStoreId = useSelector(selectStoreId)
  const storeName = useSelector(selectStoreName)
  const [branchCompareResults, setBranchCompareResults] = useState<any[]>([])
  const [fetchBranchReviewsPerformance] = useLazyFetchBranchReviewsPerformanceQuery()
  const [fetchReviewsPerformance] = useLazyFetchReviewsPerformanceQuery();

  const [
    fetchBranchBestSellingPerformance,
    fetchBranchBestSellingPerformanceQuery
  ] = useLazyFetchBranchBestSellingPerformanceQuery()

  const [
    fetchPerformancePreparationTime,
    { data: preparationTimeData, isFetching: isPreparationTimeLoading }
  ] = useLazyFetchPerformancePreparationTimeQuery()

  const [
    fetchRevenuePerformance,
    { data: revenueData, isFetching: isRevenueFetching }
  ] = useLazyFetchRevenuePerformanceQuery()

  const [
    fetchBranchRevenuePerformance,
    { data: branchRevenueData, isFetching: isBranchRevenueFetching, error }
  ] = useLazyFetchBranchRevenuePerformanceQuery()

  const [fetchBestSellingPerformance] = useLazyFetchBestSellingPerformanceQuery();

  const [activeDateRange, setActiveDateRange] = useState<CalendarRange | null>(
    null
  )
  const [mode, setMode] = useState<'single' | 'compare'>('single')
  const [branchesData, setBranchesData] = useState<
    { label: string; data: OrdersPerformanceData }[]
  >([])
  const [prepTimeData, setPrepTimeData] = useState<
    { label: string; data: number }[]
  >([])
  const [reviewBranchesData, setReviewBranchesData] = useState<
    { label: string; data: { avg_client_rating: number; avg_driver_rating: number; client_rating_details: any[]; driver_rating_details: any[] } }[]
  >([])

  // --- loading and error state ---
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [prepTimeLoading, setPrepTimeLoading] = useState(false);
  const [prepTimeError, setPrepTimeError] = useState<string | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);

  const [branchBestSellingData, setBranchBestSellingData] = useState<
    { label: string; data: BestSellingResponse }[]
  >([])

  const [bestSellingData, setBestSellingData] = useState<
    { label: string; data: BestSellingResponse }[]
  >([])
  const [bestSellingLoading, setBestSellingLoading] = useState(false)
  const [bestSellingError, setBestSellingError] = useState<string | null>(null)

  const handleRangeChange = useCallback((range: CalendarRange) => {
    setActiveDateRange(range)
  }, [])
  const [value, setValue] = useState<OptionType[]>([])

  
  const handleBranchChange = useCallback((selected: OptionType[]) => {
    setValue(selected);
    if (selected.length < 2) {
      setMode('single');
      setBranchCompareResults([]);
    }
  }, []);

 
  const handleCompare = useCallback(() => {
    if (value.length === 2 && activeDateRange) {
      setMode('compare');
    }
  }, [value, activeDateRange]);

  const revenueTimeFrame = useMemo(() => {
    if (!activeDateRange) return 'hours'

    return getTime(activeDateRange.endDate) -
      getTime(activeDateRange.startDate) >
      DAY_IN_MS
      ? 'days'
      : 'hours'
  }, [activeDateRange])

  const isSingleStore = !Array.isArray(branches) || branches.length === 0
  // Memoize currentStoreId
  const currentStoreId = useMemo(() => {
    if (isSingleStore) {
      return parentStoreId || ''
    }

    if (value.length === 0) {
      return 'all'
    }
    return value[0]?.store_id || 'all'
  }, [isSingleStore, parentStoreId, value]);

  // Memoize getBranchLabel
  const getBranchLabel = useCallback((storeId: string) => {
    if (storeId === 'all') return 'All branches';
    if (storeId === parentStoreId) {
      return storeName ?? 'Store';
    }
    const branch = branches?.find((b: any) => b.store_id === storeId);
    return branch?.restaurantname || branch?.restaurant_name_i18n?.en || branch?.area || storeName || 'Store';
  }, [branches, parentStoreId, storeName]);

  //  default date range
  useEffect(() => {
    if (!activeDateRange) {
      const today = new Date()
      const startOfToday = startOfDay(today)
      const endOfToday = endOfDay(today)
      setActiveDateRange({ startDate: startOfToday, endDate: endOfToday })
    }
  }, [activeDateRange])

  useEffect(() => {
    setBranchCompareResults([])
  }, [value])

  useEffect(() => {
    if (!activeDateRange) return;
    const { startDate, endDate } = activeDateRange;
    const isSameDate = isEqual(startOfDay(startDate), startOfDay(endDate));
    const from = getTime(isSameDate ? startOfDay(startDate) : startDate);
    const to = getTime(isSameDate ? endOfDay(endDate) : endDate);

    if (mode === 'compare' && value.length === 2) {
      switch (id) {
        case 'orders': {
          setOrdersLoading(true); setOrdersError(null);
          setPrepTimeLoading(true); setPrepTimeError(null);
    Promise.all(
      value.map(branch =>
        fetchOrdersPerformance({ from, to, store_id: branch.store_id })
          .unwrap()
                .then(res => ({
                  label: getBranchLabel(branch.store_id),
                  data: res.orderPerformance
                }))
                .catch(() => ({
                  label: getBranchLabel(branch.store_id),
                  data: {
                    received: 0,
                    rejected: { count: 0, percentage: '0%' },
                    accepted: { count: 0, percentage: '0%' },
                    cancelled: { count: 0, percentage: '0%' },
                    delivered: { count: 0, percentage: '0%' },
                    returned: { count: 0, percentage: '0%' },
                    inprogress: { count: 0, percentage: '0%' }
                  }
                }))
      )
          ).then(data => {
            setBranchesData(data);
            setOrdersLoading(false);
          }).catch(error => {
            setOrdersError(error?.message || 'An error occurred');
            setOrdersLoading(false);
          });
    Promise.all(
      value.map(branch =>
        fetchPerformancePreparationTime({ from, to, store_id: branch.store_id })
          .unwrap()
                .then(res => ({
                  label: getBranchLabel(branch.store_id),
                  data: res.preparation_time ?? 0
                }))
                .catch(() => ({
                  label: getBranchLabel(branch.store_id),
                  data: 0
                }))
      )
          ).then(data => {
            setPrepTimeData(data);
            setPrepTimeLoading(false);
          }).catch(error => {
            setPrepTimeError(error?.message || 'An error occurred');
            setPrepTimeLoading(false);
          });
          break;
        }
        case 'quality': {
          setReviewsLoading(true); setReviewsError(null);
          Promise.all(
            value.map(branch => {
              if (branch.store_id === 'all') {
                return fetchBranchReviewsPerformance({ store_id: 'all', from, to })
                  .unwrap()
                  .then((res: any) => ({
                    label: isSingleStore ? '' : 'All branches',
                    data: res
                  }))
                  .catch(() => ({
                    label: isSingleStore ? '' : 'All branches',
                    data: {
                      avg_client_rating: -1,
                      avg_driver_rating: -1,
                      client_rating_details: [],
                      driver_rating_details: []
                    }
                  }));
              } else {
                return fetchBranchReviewsPerformance({ store_id: branch.store_id, from, to })
                  .unwrap()
                  .then((res: any) => ({
                    label: getBranchLabel(branch.store_id),
                    data: res
                  }))
                  .catch(() => ({
                    label: getBranchLabel(branch.store_id),
                    data: {
                      avg_client_rating: -1,
                      avg_driver_rating: -1,
                      client_rating_details: [],
                      driver_rating_details: []
                    }
                  }));
              }
            })
          ).then(data => {
            setReviewBranchesData(data);
            setReviewsLoading(false);
          }).catch(error => {
            setReviewsError(error?.message || 'An error occurred');
            setReviewsLoading(false);
          });
          // Best selling for compare mode
          setBestSellingLoading(true); setBestSellingError(null);
          Promise.all(
            value.map(branch =>
              fetchBranchBestSellingPerformance({ from, to, store_id: branch.store_id })
                .unwrap()
                .then(res => ({
                  label: getBranchLabel(branch.store_id),
                  data: res
                }))
                .catch(() => ({
                  label: getBranchLabel(branch.store_id),
                  data: { bestSellingPerformance: [] }
                }))
            )
          ).then(data => {
            setBestSellingData(data);
            setBestSellingLoading(false);
          }).catch(error => {
            setBestSellingError(error?.message || 'An error occurred');
            setBestSellingLoading(false);
          });
          break;
        }
        case 'revenue': {
         
          setBranchCompareResults([]);
         
          value.forEach(async branch => {
                const result = await fetchBranchRevenuePerformance({ from, to, store_id: branch.store_id }).unwrap();
                  if (branch.store_id !== parentStoreId) {
                    const matchedBranch = branches?.find(b => b.store_id === branch.store_id);
                    const branchWithArea = {
                      ...result,
                      store_id: matchedBranch?.store_id,
                      area: matchedBranch?.area,
                      address: matchedBranch?.address
                    };
                    setBranchCompareResults(prev => [...prev, branchWithArea]);
                  } else {
                    const branchWithArea = {
                      ...result,
                      store_id: parentStoreId,
                      area: storeName,
                      address: storeName
                    };
                    setBranchCompareResults(prev => [...prev, branchWithArea]);
                  }
              });
          break;
        }
      }
      return;
    }

    if (mode === 'single') {
      switch (id) {
        case 'orders': {
          if (store_web_app_branches_performance) {
            fetchOrdersPerformance({ from, to, store_id: currentStoreId })
              .unwrap()
              .then(res => {
                setBranchesData([
                  {
                    label: getBranchLabel(currentStoreId),
                    data: res.orderPerformance
                  }
                ]);
              })
              .catch(error => {
                setBranchesData([
                  {
                    label: getBranchLabel(currentStoreId),
                    data: {
                      received: 0,
                      rejected: { count: 0, percentage: '0%' },
                      accepted: { count: 0, percentage: '0%' },
                      cancelled: { count: 0, percentage: '0%' },
                      delivered: { count: 0, percentage: '0%' },
                      returned: { count: 0, percentage: '0%' },
                      inprogress: { count: 0, percentage: '0%' }
                    }
                  }
                ])
              })
            fetchPerformancePreparationTime({ from, to, store_id: currentStoreId })
              .unwrap()
              .then(res => {
                setPrepTimeData([
                  {
                    label: getBranchLabel(currentStoreId),
                    data: res.preparation_time ?? 0
                  }
                ])
              })
              .catch(error => {
                setPrepTimeData([
                  { label: getBranchLabel(currentStoreId), data: 0 }
                ]);
              });
          } else {
            fetchOrdersPerformanceOld({ from, to })
              .unwrap()
              .then(res => {
                setBranchesData([
                  {
                    label: '',
                    data: res.orderPerformance
                  }
                ])
              })
              .catch(error => {
                setBranchesData([
                  {
                    label: '',
                    data: {
                      received: 0,
                      rejected: { count: 0, percentage: '0%' },
                      accepted: { count: 0, percentage: '0%' },
                      cancelled: { count: 0, percentage: '0%' },
                      delivered: { count: 0, percentage: '0%' },
                      returned: { count: 0, percentage: '0%' },
                      inprogress: { count: 0, percentage: '0%' }
                    }
                  }
                ])
              })
            fetchPreparationTimePerformanceOld({ from, to })
              .unwrap()
              .then(res => {
                setPrepTimeData([
                  {
                    label: '',
                    data: res.preparation_time ?? 0
                  }
                ])
              })
              .catch(error => {
                setPrepTimeData([
                  { label: '', data: 0 }
                ])
              })
          }
          break
        }
        case 'quality': {
          if (!currentStoreId) return;
          setReviewsLoading(true); setReviewsError(null);
          setBestSellingLoading(true); setBestSellingError(null);
          if (store_web_app_branches_performance) {
            fetchBranchReviewsPerformance({ store_id: currentStoreId === 'all' ? 'all' : currentStoreId, from, to })
              .unwrap()
              .then(res => {
                setReviewBranchesData([
                  {
                    label: getBranchLabel(currentStoreId),
                    data: res
                  }
                ]);
                setReviewsLoading(false);
              })
              .catch((error) => {
                setReviewBranchesData([
                  {
                    label: getBranchLabel(currentStoreId),
                    data: {
                      avg_client_rating: -1,
                      avg_driver_rating: -1,
                      client_rating_details: [],
                      driver_rating_details: []
                    }
                  }
                ]);
                setReviewsLoading(false);
              });
            fetchBranchBestSellingPerformance({ from, to, store_id: currentStoreId })
              .unwrap()
              .then(res => {
                setBestSellingData([
                  {
                    label: getBranchLabel(currentStoreId),
                    data: res
                  }
                ])
                setBestSellingLoading(false)
              })
              .catch(error => {
                setBestSellingError(error?.message || 'An error occurred')
                setBestSellingLoading(false)
              })
          } else {
            fetchReviewsPerformance({ from, to })
              .unwrap()
              .then(res => {
                setReviewBranchesData([
                  {
                    label: '',
                    data: res
                  }
                ]);
                setReviewsLoading(false);
              })
              .catch((error) => {
                setReviewBranchesData([
                  {
                    label: '',
                    data: {
                      avg_client_rating: -1,
                      avg_driver_rating: -1,
                      client_rating_details: [],
                      driver_rating_details: []
                    }
                  }
                ]);
                setReviewsLoading(false);
              });
            fetchBestSellingPerformance({ from, to })
              .unwrap()
              .then(res => {
                setBestSellingData([
                  {
                    label: '',
                    data: res
                  }
                ])
                setBestSellingLoading(false)
              })
              .catch((error: { message: any }) => {
                setBestSellingError(error?.message || 'An error occurred')
                setBestSellingLoading(false)
              })
          }
          break;
        }
        case 'revenue': {
          fetchRevenuePerformance({ from, to })
          fetchBranchRevenuePerformance({ store_id: currentStoreId, from, to });
          break;
        }
        default: {
          break
        }
      }
    }
  }, [
    mode,
    value,
    activeDateRange,
    id,
    fetchOrdersPerformance,
    fetchPerformancePreparationTime,
    fetchBranchRevenuePerformance,
    fetchOrdersPerformanceOld,
    fetchPreparationTimePerformanceOld,
    fetchRevenuePerformance,
    fetchBranchReviewsPerformance,
    fetchBranchBestSellingPerformance,
    branches,
    parentStoreId,
    storeName,
    currentStoreId,
    store_web_app_branches_performance,
    getBranchLabel,
    isSingleStore,
    fetchReviewsPerformance,
    fetchBestSellingPerformance
  ])

  const isRevenueEmpty = useMemo(() => {
    if (isRevenueFetching || isBranchRevenueFetching) return false;
    
    // If data is empty
    if (!revenueData && !branchRevenueData && branchCompareResults.length === 0) {
      return true;
    }
    
    let data = store_web_app_branches_performance
      ? branchCompareResults.length > 0
        ? branchCompareResults
        : [branchRevenueData]
      : [revenueData];
    
    // Filter out null/undefined entries
    data = data.filter(Boolean);
    
    // IF data it's nested
    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
      data = data[0];
    }
    
    // If data is undefined/null
    if (!data || data.length === 0) {
      return true;
    }
    
    // Show revenue data if the structure exists (even if values are zero)
    // The RevenuePerformance component will handle displaying zero values appropriately
    return false;
  }, [
    store_web_app_branches_performance,
    branchCompareResults,
    branchRevenueData,
    revenueData,
    isRevenueFetching,
    isBranchRevenueFetching,
  ]);

  const isQualityEmpty = useMemo(() => {
    if (reviewsLoading || bestSellingLoading) return false;
    
    // Check if reviews data exists and is valid
    const hasReviewsData = reviewBranchesData.length > 0 && 
      reviewBranchesData.some(
        branch => 
          branch.data.avg_client_rating !== -1 || 
          branch.data.avg_driver_rating !== -1
      );
    
    // Check if best selling data exists and is valid
    const hasBestSellingData = bestSellingData.length > 0 && 
      bestSellingData.some(
        branch => 
          branch.data?.bestSellingPerformance?.length > 0
      );
    
    // Quality tab is empty only if both reviews and best selling are empty
    return !hasReviewsData && !hasBestSellingData;
  }, [reviewBranchesData, bestSellingData, reviewsLoading, bestSellingLoading]);

  return (
    <div className={Styles.page}>
      <div className={Styles.extendedHeader}>
        <Header title={t('common.page-names.performance')} />
        {store_web_app_branches_performance && Array.isArray(branches) && (
          <Banner
            description="Below you can find the statistics of each branch as well as compare their performances."
            icon={<InfoCircle />}
            type="plain"
            state="normal"
          />
        )}
        <div className={Styles.tabs}>
          <ContentBlock className={Styles.tabFilters}>
          {store_web_app_branches_performance && Array.isArray(branches) && branches.length > 0 && (
            <div className={Styles.filterItem}>
              <Text variant="heading-05">Filter</Text>
              <div className={Styles.branchRow}>
                {value.length > 0 && (
                  <span className={Styles.selectedBranchName}>
                    <span className={Styles.icon}><Store /></span>
                    {value.length === 2 ? 'Branches' : "Branche"}
                  </span>
                )}
                <ComparisonSelector
                  value={value}
                  onChange={handleBranchChange}
                  options={
                    Array.isArray(branches)
                      ? [
                          {
                            store_id: parentStoreId || '',
                            area: storeName || '',
                            restaurantname: storeName || ''
                          },
                          ...branches
                        ]
                      : []
                  }
                  onCompare={handleCompare}
                  disabled={value.length !== 2}
                />
              </div>
            </div>
            )}
            <div className={Styles.filterItemInfo}>
              <CalendarFilled />
              <Text variant="subtitle">Date Range</Text>
            </div>
            <div className={Styles.fullSm}>
              <DateRangeSelect
                onChange={handleRangeChange}
                defaultValue={DEFAULT_PERIOD}
              />
            </div>
          </ContentBlock>
          <Tabs
            asNavigation
            wrapperClassName={Styles.wrapperFull}
            tabClassName={Styles.tabCentered}>
            <Tab id="/performance/orders">{t('performance.tabs.orders')}</Tab>
            <Tab id="/performance/revenue">{t('performance.tabs.revenue')}</Tab>
            <Tab id="/performance/quality">{t('performance.tabs.quality')}</Tab>
          </Tabs>
        </div>
      </div>
      <div className={Styles.contentk}>
        {id === 'orders' && (
          branchesData.length > 0 && branchesData[0].data?.received === 0 ? (
            <EmptyPerformance />
          ) : (
            store_web_app_branches_performance ? (
              <>
                {/* Branch comparison */}
                <Text
                  className={Styles.sectionTitle}
                  variant="nextTitle-2"
                  weight="semiBold"
                  asChild>
                  <h2>{t('performance.orders-performance.title')}</h2>
                </Text>
                {ordersLoading ? (
                  <LoadingSpinner 
                    containerType="centered" 
                    text={t('performance.loading.orders', 'Loading orders data...')}
                  />
                ) : ordersError ? (
                  <Text color="danger">{ordersError}</Text>
                ) : (
                  <OrdersPerformance
                    isLoading={false}
                    isFetching={false}
                    mode={mode}
                    branches={branchesData}
                    dateRange={activeDateRange}
                    storeId={currentStoreId === 'all' ? parentStoreId || '' : currentStoreId}
                    isAllBranches={
                      (currentStoreId === 'all' && Array.isArray(branches) && branches.length > 0) ||
                      isSingleStore
                    }
                  />
                )}

                <Text
                  className={Styles.sectionTitle}
                  variant="nextTitle-2"
                  weight="semiBold"
                  asChild>
                  <h2>{t('performance.prep-time.title')}</h2>
                </Text>
                {prepTimeLoading ? (
                  <LoadingSpinner 
                    containerType="centered" 
                    text={t('performance.loading.prepTime', 'Loading preparation time...')}
                  />
                ) : mode === 'compare' ? (
                  <div className={Styles.branchCard}>
                    {prepTimeData.map((branch, index) => (
                      <div key={branch.label} className={Styles.branchCard2}>
                        <PreparationTime
                          title={branch.label}
                          className={Styles.blockWithSpacing}
                          isLoading={isPreparationTimeLoading}
                          preparationTime={branch.data}
                        />
                        {index === 0 && prepTimeData.length > 1 && (
                          <div className={Styles.comparisonSeparator} />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <PreparationTime
                    title={
                      prepTimeData[0]?.label ??
                      (isSingleStore ? '' : 'All branches')
                    }
                    className={Styles.blockWithSpacing}
                    isLoading={isPreparationTimeLoading}
                    preparationTime={prepTimeData[0]?.data ?? 0}
                  />
                )}
              </>
            ) : (
              <>
                {/*  single-store orders performance only */}
                <Text
                  className={Styles.sectionTitle}
                  variant="nextTitle-2"
                  weight="semiBold"
                  asChild>
                  <h2>{t('performance.orders-performance.title')}</h2>
                </Text>
                <OrdersPerformance
                  isLoading={false}
                  isFetching={false}
                  mode="single"
                  branches={branchesData.map(branch => ({
                    ...branch,
                    label: (!store_web_app_branches_performance || isSingleStore) ? '' : branch.label
                  }))}
                  dateRange={activeDateRange}
                  storeId={parentStoreId || ''}
                />
                <Text
                  className={Styles.sectionTitle}
                  variant="nextTitle-2"
                  weight="semiBold"
                  asChild>
                  <h2>{t('performance.prep-time.title')}</h2>
                </Text>
                <PreparationTime
                  title={
                    prepTimeData[0]?.label ??
                    (!store_web_app_branches_performance || isSingleStore) ? '' : 'All branches'
                  }
                  className={Styles.blockWithSpacing}
                  isLoading={isPreparationTimeLoading}
                  preparationTime={prepTimeData[0]?.data ?? 0}
                />
              </>
            )
          )
        )}
        {id === 'quality' && (
          (reviewsLoading || bestSellingLoading) ? (
            <LoadingSpinner 
              containerType="centered" 
              text={t('performance.loading.quality', 'Loading quality data...')}
            />
          ) : (reviewsError || bestSellingError) ? (
            <Text color="danger">{reviewsError || bestSellingError}</Text>
          ) : isQualityEmpty ? (
            <EmptyPerformance />
          ) : (
            <>
              <ReviewsPerformance
                mode={mode}
                branches={reviewBranchesData}
              />
              <Text
                variant="nextTitle-2"
                weight="semiBold"
                className={clsx(Styles.sectionTitle, Styles.sectionTopMargin)}
                asChild
              >
                <h2>{t('performance.best-selling.title')}</h2>
              </Text>
              {bestSellingLoading ? (
                <LoadingSpinner 
                  containerType="centered" 
                  text={t('performance.loading.bestSelling', 'Loading best selling data...')}
                />
              ) : (
                <BestSellingPerformance
                  branches={bestSellingData}
                  isLoading={false}
                  error={bestSellingError}
                />
              )}
            </>
          )
        )}
        {id === 'revenue' && (
          (store_web_app_branches_performance ? isBranchRevenueFetching : isRevenueFetching) ? (
            <LoadingSpinner 
              containerType="centered" 
              text={t('performance.loading.revenue', 'Loading revenue data...')}
            />
          ) : isRevenueEmpty ? (
            <EmptyPerformance />
          ) : (
            <RevenuePerformance
              data={
                store_web_app_branches_performance
                  ? branchCompareResults.length > 0
                    ? branchCompareResults
                    : [branchRevenueData]
                  : [revenueData]
              }
              isLoading={
                store_web_app_branches_performance
                  ? isBranchRevenueFetching
                  : isRevenueFetching
              }
              timeframe={revenueTimeFrame}
              multiple={branchCompareResults.length > 1}
            />
          )
        )}
      </div>
    </div>
  )
}

export default PerformancePage
